<template>
  <div>
    <div
      v-if="!editing && ['view', 'both'].includes(method)"
      class="px-2 py-1 bg-gray-200 text-gray-700 rounded-lg cursor-pointer flex justify-between items-center min-h-[28px]"
      @click="method == 'both' ? startEditMode($event) : null"
      @dblclick="method == 'both' ? startEditMode($event) : null"
    >
      <span
        v-if="newValue && customField.field_type === 'multiple_options'"
        class="flex justify-start items-center gap-1"
      >
        <span v-for="(option, index) in newValue" :key="index" class="badge badge-default">
          {{ option }}
        </span>
      </span>
      <div
        v-else-if="customField.field_type === 'rich_text'"
        class="user-generated-content text-sm mt-1 break-words whitespace-pre-line w-full flex-1 min-w-0"
        v-html="sanitize(newValue as string)"
      ></div>
      <span v-else class="flex items-center jusitfy-between gap-1">
        {{ visibleValue }}&nbsp;
        <a v-if="isUrl" :href="newValue as string" target="_blank" rel="noopener noreferrer">
          <vue-feather type="external-link" size="16" />
        </a>
      </span>

      <button v-if="customField.sensitive" class="flex items-center gap-1" @click="getSensitive">
        <vue-feather v-if="secureValue" type="eye" size="16" />
        <vue-feather v-else type="eye-off" size="16" />
      </button>
    </div>
    <div v-else-if="['edit', 'both'].includes(method)" ref="customFieldWrapper" class="w-full custom-field-editor">
      <form @submit.prevent="saveCustomFieldValue">
        <QuillInput
          v-if="customField.field_type === 'rich_text'"
          v-model="newValue as string"
          :name="customField.name"
          :label="customField.name"
          :label-sr-only="true"
          @blur="saveCustomFieldValue"
        />
        <AddressInput
          v-else-if="customField.field_type === 'address'"
          v-model="newValue as string"
          :hide-label="true"
          @update:model-value="saveCustomFieldValue"
        />
        <DatePicker
          v-else-if="customField.field_type === 'date'"
          v-model="newValue as string"
          @update:model-value="saveCustomFieldValue"
        />
        <div v-else-if="customField.field_type === 'multiple_options'" class="flex flex-col gap-2">
          <label v-for="option in customField.options" :key="option" class="flex items-center gap-2 cursor-pointer">
            <Checkbox
              :model-value="(newValue as string[]).includes(option)"
              @update:model-value="toggleCheckbox(option, $event as boolean)"
            />
            <span class="text-sm">{{ option }}</span>
          </label>
        </div>
        <Select
          v-else-if="customField.field_type === 'single_option'"
          v-model="newValue as string"
          @update:model-value="saveCustomFieldValue"
        >
          <SelectTrigger class="w-full">
            <SelectValue placeholder="Select an option" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="option in customField.options" :key="option" :value="option">
              {{ option }}
            </SelectItem>
          </SelectContent>
        </Select>
        <Input
          v-else
          ref="customFieldInput"
          v-model="newValue as string"
          :type="customFieldInputType === 'number' ? 'number' : 'text'"
          @keydown.enter="saveCustomFieldValue"
        />
        <Button
          v-if="displaySubmitButton && !['rich_text', 'address'].includes(customField.field_type)"
          type="submit"
          class="mt-2"
        >
          Save
        </Button>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { formatStandardDate, formatCurrencyToHuman } from '../../utilities/format.ts';
import QuillInput from '../shared/QuillInput.vue';
import { sanitizeRichText } from '../../utilities/sanitization.ts';
import AddressInput from '../shared/AddressInput.vue';
import { toast } from 'vue-sonner';
import type { CustomFieldEntity } from '@/api/entity_types/CustomField.ts';
import type { CustomFieldableType, CustomFieldValueEntity } from '@/api/entity_types/CustomFieldValue.ts';
import { Api } from '@/api/index.ts';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date_picker';

type CustomFieldMethod = 'edit' | 'view' | 'both';

export type CustomFieldValueUpdatedPayload = {
  custom_field_value: {
    key: string;
    custom_fieldable_type: string;
    custom_fieldable_id: string | number;
    custom_field_id: number;
    value: string;
  };
};

type Props = {
  customField: CustomFieldEntity;
  customFieldValue?: Partial<CustomFieldValueEntity & { custom_field: CustomFieldEntity }>;
  customFieldableType: CustomFieldableType;
  customFieldableId: number;
  method: CustomFieldMethod;
  displaySubmitButton?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  customFieldValue: undefined,
  displaySubmitButton: false,
});

const emit = defineEmits<{
  updated: [payload: CustomFieldValueUpdatedPayload];
}>();

const editing = ref(false);
const newValue = ref<string | string[]>('');
const secureValue = ref<string | null>(null);
const initialValue = ref<string | string[]>('');
const customFieldInput = ref<InstanceType<typeof Input> | null>(null);

const customFieldInputType = computed(() => {
  switch (props.customField.field_type) {
    case 'number':
    case 'percentage':
    case 'currency':
      return 'number';
    default:
      return 'text';
  }
});

const visibleValue = computed(() => {
  if (props.customField.sensitive && !props.customFieldValue && !newValue.value) {
    return '';
  } else if (props.customField.sensitive && !secureValue.value) {
    return '**************';
  } else if (props.customField.sensitive && secureValue.value) {
    return secureValue.value;
  } else if (props.customField.field_type === 'date' && newValue.value) {
    return formatStandardDate(newValue.value as string);
  } else if (props.customField.field_type === 'currency' && newValue.value) {
    return formatCurrencyToHuman(newValue.value as string, 2);
  } else if (props.customField.field_type === 'percentage' && newValue.value) {
    return `${newValue.value}%`;
  }
  return newValue.value;
});

const isUrl = computed(() => {
  const regex = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;
  return regex.test(newValue.value as string);
});

watch(newValue, newVal => {
  emit('updated', {
    custom_field_value: {
      key: props.customField.name,
      custom_fieldable_type: props.customFieldableType,
      custom_fieldable_id: props.customFieldableId,
      custom_field_id: props.customField.id,
      value:
        props.customField.field_type === 'multiple_options' && Array.isArray(newVal)
          ? newVal.length
            ? newVal.join(',')
            : ''
          : [newVal].flat().join(','),
    },
  });
});

onMounted(() => {
  const val = props.customFieldValue?.value || '';
  if (props.customField.field_type === 'multiple_options') {
    newValue.value = val ? val.split(',') : [];
  } else {
    newValue.value = val;
  }
  initialValue.value = newValue.value;
});

const toggleCheckbox = (option: string, checked: boolean) => {
  const arr = newValue.value as string[];
  newValue.value = checked ? [...arr, option] : arr.filter(v => v !== option);
};

const getSensitive = async (e: Event) => {
  e.stopPropagation();
  if (!props.customFieldValue?.id) return;

  if (secureValue.value) {
    secureValue.value = null;
    return;
  }
  const response = await Api.CustomFieldValue.decryptCustomFieldValue(props.customFieldValue.id);

  secureValue.value = response.record.secure_value;
};

const startEditMode = async (e: Event) => {
  if (props.customField.sensitive) {
    secureValue.value = null;
    await getSensitive(e);
    newValue.value = secureValue.value || '';
  }

  if (props.customField.field_type === 'single_option' && newValue.value === '') {
    newValue.value = props.customField.options?.[0] || '';
  }

  editing.value = true;

  nextTick(() => {
    (customFieldInput.value?.$el as HTMLInputElement)?.focus();
  });
};

const saveCustomFieldValue = async () => {
  if (props.method !== 'both') return;

  editing.value = false;
  secureValue.value = null;

  const payload = {
    custom_fieldable_type: props.customFieldableType,
    custom_fieldable_id: props.customFieldableId,
    custom_field_id: props.customField.id,
    value: [newValue.value].flat().join(','),
  };

  const data = await Api.CustomFieldValue.updateCustomFieldValue({ payload });

  if (data.status === 'error') {
    toast.error('Custom Field Update Error', {
      action: { label: 'Dismiss' },
      description: data.message,
    });
  }

  initialValue.value = Array.isArray(newValue.value) ? [...newValue.value] : newValue.value;

  emit('updated', { custom_field_value: { ...payload, key: props.customField.name } });
};

const sanitize = (html: string) => sanitizeRichText(html);
</script>
